/**
 * Вкладка «Порты»: ночной срез состояния портов по всей сети.
 *
 * Заменяет отчёт Power BI «Общая информация по коммутаторам on-line»
 * и «Устройства на портах коммутаторов». Сводку и поиск считает сервер:
 * по сети это десятки тысяч строк. Шкаф и расположение берутся из учёта
 * коммутаторов, а не из среза.
 *
 * Под заголовком каждой таблицы — строка фильтров по столбцам. Таблица
 * коммутаторов загружена целиком и фильтруется здесь же; устройств
 * бывает сотни тысяч, поэтому их фильтры уходят на сервер.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { api, downloadFile } from '../../api/client';
import { selectGrouped, selectPrefixMap } from '../../store/slices/switchesSlice';
import { selectDivisions } from '../../store/slices/divisionsSlice';
import { selectCanEdit } from '../../store/slices/authSlice';
import { objectLabel } from '../../domain/issues';
import { vlanColor } from '../../domain/ports';
import { STATUS_META, splitMacs, utilization } from '../../domain/portStatus';
import {
  EMPTY_SWITCH_FILTERS,
  EMPTY_DEVICE_FILTERS,
  hasAny,
  isBadNum,
  matchSwitchRow,
  objectMatch,
  deviceFilterParams,
} from '../../domain/portFilters';
import { selectVlanMap } from '../../store/slices/vlansSlice';
import { vlanText, resolveVlanFilter } from '../../domain/vlans';
import { usePortsImport } from './usePortsImport';
import VlanDirectory from './VlanDirectory';
import SwitchPorts from './SwitchPorts';
import PageHeader from '../../components/common/PageHeader';
import Icon from '../../components/common/Icon';
import ui from '../../components/common/ui.module.css';
import styles from './PortsPage.module.css';

const STATUS_CLASS = { UP: 'stUP', DOWN: 'stDOWN', ADM: 'stADM' };
const today = () => new Date().toISOString().slice(0, 10);
const formatDate = (iso) => (iso ? iso.split('-').reverse().join('.') : '—');
/** Значение фильтра «Расположение»: коммутаторы, у которых оно не заполнено. */
const NO_LOCATION = '__none__';
const byRu = (a, b) => a.localeCompare(b, 'ru', { numeric: true });

const TEXT_HINT = 'Поиск по вхождению. «-» — значение не указано';
const NUM_HINT = 'Число или выражение: 5, >10, <=3, 20-80';

/** Поле фильтра в заголовке столбца. */
function ColFilter({ value, onChange, placeholder, numeric = false, label }) {
  const bad = numeric && isBadNum(value);
  return (
    <input
      className={`${styles.colFilter} ${bad ? styles.colFilterBad : ''} ${value ? styles.colFilterOn : ''}`}
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      title={bad ? 'Не понял выражение. ' + NUM_HINT : numeric ? NUM_HINT : TEXT_HINT}
      aria-label={`Фильтр: ${label}`}
    />
  );
}

export default function PortsPage() {
  const grouped = useSelector(selectGrouped);
  const prefixMap = useSelector(selectPrefixMap);
  const divisions = useSelector(selectDivisions);
  const canEdit = useSelector(selectCanEdit);
  const vlanMap = useSelector(selectVlanMap);
  const [vlanDirOpen, setVlanDirOpen] = useState(false);

  const [view, setView] = useState('switches');
  const [division, setDivision] = useState('');
  const [mag, setMag] = useState('');
  const [vlan, setVlan] = useState('');
  const [status, setStatus] = useState('');
  const [cabinet, setCabinet] = useState('');
  const [location, setLocation] = useState('');
  const [colF, setColF] = useState(EMPTY_SWITCH_FILTERS);
  const [devF, setDevF] = useState(EMPTY_DEVICE_FILTERS);
  const [devFq, setDevFq] = useState(EMPTY_DEVICE_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [summary, setSummary] = useState({ state: 'loading' });
  const [devices, setDevices] = useState({ state: 'idle', rows: [] });

  const fileRef = useRef(null);
  const [snapshotDate, setSnapshotDate] = useState(today());
  const importer = usePortsImport(() => setReloadKey((k) => k + 1));

  // Поиск — с задержкой, чтобы не обращаться к серверу на каждую букву
  useEffect(() => {
    const t = setTimeout(() => setQ(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Фильтры столбцов устройств тоже уходят на сервер — с той же задержкой
  useEffect(() => {
    const t = setTimeout(() => setDevFq(devF), 300);
    return () => clearTimeout(t);
  }, [devF]);
  const setCol = (k) => (v) => setColF((f) => ({ ...f, [k]: v }));
  const setDev = (k) => (v) => setDevF((f) => ({ ...f, [k]: v }));

  // Дивизион и объект отбираются на сервере — списком объектов
  const mags = useMemo(() => {
    if (mag) return [mag];
    if (division) return divisions[division] || [];
    return [];
  }, [mag, division, divisions]);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (mags.length) p.set('mags', mags.join(','));
    if (vlan) p.set('vlan', vlan);
    if (status) p.set('status', status);
    if (q.length >= 2) p.set('q', q);
    return p.toString();
  }, [mags, vlan, status, q]);

  useEffect(() => {
    let cancelled = false;
    setSummary((s) => ({ ...s, state: s.data ? 'refreshing' : 'loading' }));
    api
      .get(`/api/ports/summary?${query}`)
      .then((data) => {
        if (!cancelled) setSummary({ state: 'ready', data });
      })
      .catch((e) => {
        if (!cancelled) setSummary({ state: 'error', message: e.message });
      });
    return () => {
      cancelled = true;
    };
  }, [query, reloadKey]);

  // Сведения из учёта: шкаф и расположение — по объекту и имени
  const inventory = useMemo(() => {
    const map = new Map();
    for (const [m, rows] of Object.entries(grouped)) {
      for (const r of rows) map.set(`${m}__${r['Коммутатор']}`, r);
    }
    return map;
  }, [grouped]);

  const data = summary.data;
  const enriched = useMemo(
    () =>
      (data?.switches || []).map((s) => {
        const inv = inventory.get(`${s.mag}__${s.sw}`);
        return {
          ...s,
          cabinet: inv?.['Шкаф'] || '',
          location: inv?.['Расположение'] || '',
          model: inv?.['Модель SW'] || '',
          inInventory: Boolean(inv),
        };
      }),
    [data, inventory]
  );

  const locationMatch = (loc) => !location || (location === NO_LOCATION ? !loc : loc === location);

  // Шкаф и расположение — из учёта, поэтому отбираются здесь, как и
  // фильтры столбцов: таблица коммутаторов загружена целиком
  const visible = enriched.filter(
    (s) =>
      (!cabinet || s.cabinet === cabinet) &&
      locationMatch(s.location) &&
      matchSwitchRow(s, colF, objectLabel(s.mag, prefixMap), utilization(s))
  );

  const kpi = visible.reduce(
    (a, s) => ({
      total: a.total + s.total,
      up: a.up + s.up,
      down: a.down + s.down,
      adm: a.adm + s.adm,
      devices: a.devices + s.devices,
    }),
    { total: 0, up: 0, down: 0, adm: 0, devices: 0 }
  );
  const withoutLocation = enriched.filter((s) => !s.location).length;

  const objectOptions = useMemo(() => [...new Set(enriched.map((s) => s.mag))].sort(), [enriched]);
  const cabinetOptions = [...new Set(enriched.map((s) => s.cabinet).filter(Boolean))].sort(byRu);
  // Расположения — с учётом выбранного шкафа: иначе в списке были бы
  // варианты, под которые ничего не попадёт
  const locationOptions = [
    ...new Set(
      enriched
        .filter((s) => !cabinet || s.cabinet === cabinet)
        .map((s) => s.location)
        .filter(Boolean)
    ),
  ].sort(byRu);

  // Запрос устройств: общие фильтры + шкаф, расположение и столбцы.
  // Столбец «Объект» превращается в список объектов — на сервере объект
  // хранится без префикса, а пользователь видит и вводит его с префиксом.
  const devObjects = useMemo(() => {
    if (!devFq.object.trim()) return null;
    const base = mags.length ? mags : objectOptions;
    return base.filter((m) => objectMatch(objectLabel(m, prefixMap), m, devFq.object));
  }, [devFq.object, mags, objectOptions, prefixMap]);

  // В фильтре VLAN можно написать назначение — «кассы» превращается в номер
  const devVlan = useMemo(() => resolveVlanFilter(devFq.vlan, vlanMap), [devFq.vlan, vlanMap]);

  const devicesQuery = useMemo(() => {
    const p = new URLSearchParams(query);
    if (devObjects) p.set('mags', devObjects.join(','));
    if (cabinet) p.set('cabinet', cabinet);
    if (location === NO_LOCATION) p.set('noloc', '1');
    else if (location) p.set('location', location);
    for (const [k, v] of Object.entries(deviceFilterParams({ ...devFq, vlan: devVlan ?? '' })))
      p.set(k, v);
    return p.toString();
  }, [query, devObjects, cabinet, location, devFq, devVlan]);
  // Пусто по объекту или по назначению VLAN — сервер не спрашиваем
  const devicesNone = Boolean(devObjects && devObjects.length === 0) || devVlan === null;

  useEffect(() => {
    // Под фильтр объекта не попал ни один объект — спрашивать сервер незачем
    if (view !== 'devices' || devicesNone) return undefined;
    let cancelled = false;
    setDevices((d) => ({ ...d, state: 'loading' }));
    api
      .get(`/api/ports/devices?${devicesQuery}`)
      .then((res) => {
        if (!cancelled) setDevices({ state: 'ready', ...res });
      })
      .catch((e) => {
        if (!cancelled) setDevices({ state: 'error', message: e.message, rows: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [view, devicesQuery, devicesNone, reloadKey]);
  const devList = devicesNone ? { state: 'ready', rows: [], total: 0 } : devices;

  // ── Выгрузка в CSV ──
  // Уходит то, что видно: общие фильтры, шкаф и расположение. Во вкладке
  // «Устройства» — ещё фильтры столбцов (их понимает сервер). Во вкладке
  // «Коммутаторы» фильтры столбцов считаются здесь, поэтому сервер
  // получает список отобранных коммутаторов.
  const [exporting, setExporting] = useState(null);
  async function exportCsv() {
    const filters = Object.fromEntries(
      new URLSearchParams(view === 'devices' ? devicesQuery : query)
    );
    if (view !== 'devices') {
      if (cabinet) filters.cabinet = cabinet;
      if (location === NO_LOCATION) filters.noloc = '1';
      else if (location) filters.location = location;
    }
    const narrowed = view === 'switches' && hasAny(colF);
    if ((narrowed && visible.length === 0) || (view === 'devices' && devicesNone)) {
      setExporting({ error: 'Под фильтры ничего не попало — выгружать нечего.' });
      return;
    }
    setExporting({ busy: true });
    try {
      await downloadFile('/api/ports/export', `порты-${today()}.csv`, {
        filters,
        switches: narrowed ? visible.map((s) => ({ mag: s.mag, sw: s.sw })) : undefined,
      });
      setExporting(null);
    } catch (e) {
      setExporting({ error: e.message });
    }
  }
  const vlanOptions = [...(data?.vlans || [])].sort(
    (a, b) => Number(a.vlan) - Number(b.vlan) || String(a.vlan).localeCompare(String(b.vlan))
  );

  const anyFilter =
    division ||
    mag ||
    vlan ||
    status ||
    cabinet ||
    location ||
    searchInput ||
    hasAny(colF) ||
    hasAny(devF);
  const resetFilters = () => {
    setDivision('');
    setMag('');
    setVlan('');
    setStatus('');
    setCabinet('');
    setLocation('');
    setSearchInput('');
    setColF(EMPTY_SWITCH_FILTERS);
    setDevF(EMPTY_DEVICE_FILTERS);
  };
  const noLocation = location === NO_LOCATION;

  const latest = data?.totals?.latest;
  const oldest = data?.totals?.oldest;
  const ageDays = latest ? Math.floor((Date.now() - new Date(latest).getTime()) / 86400000) : null;
  const nothingLoaded = summary.state === 'ready' && !anyFilter && !data?.totals?.total;

  const share = (n) => (kpi.total ? (n / kpi.total) * 100 : 0);
  const statusLegend = (key, value) => (
    <button
      className={`${styles.legendItem} ${status === key ? styles.legendActive : ''}`}
      onClick={() => setStatus(status === key ? '' : key)}
      aria-pressed={status === key}
      title="Показать только такие порты"
    >
      <span className={`${styles.legendDot} ${styles[STATUS_CLASS[key]]}`} />
      <span className={styles.legendLabel}>{STATUS_META[key].label}</span>
      <span className={styles.legendValue}>{value.toLocaleString('ru')}</span>
    </button>
  );

  return (
    <div className={styles.page}>
      <PageHeader
        title="Порты"
        subtitle={
          latest ? (
            <>
              Срез на {formatDate(latest)}
              {oldest && oldest !== latest && <>, по части коммутаторов с {formatDate(oldest)}</>}
              {ageDays > 1 && (
                <span className={styles.stale}>. Новый срез не приходил {ageDays} дн.</span>
              )}
            </>
          ) : (
            'Срез ещё не загружен'
          )
        }
        actions={
          <>
            <button
              className={ui.button}
              onClick={() => setVlanDirOpen(true)}
              title={
                canEdit
                  ? 'Назначение VLAN по номеру — просмотр и правка'
                  : 'Назначение VLAN по номеру'
              }
            >
              Справочник VLAN
            </button>
            {canEdit && (
              <div className={styles.importBox}>
                <input
                  type="date"
                  className={`${ui.search} ${styles.dateInput}`}
                  value={snapshotDate}
                  onChange={(e) => setSnapshotDate(e.target.value)}
                  aria-label="Дата среза"
                  title="Дата среза"
                />
                <button
                  className={`${ui.button} ${ui.primary}`}
                  onClick={() => fileRef.current?.click()}
                  disabled={
                    importer.state.status === 'reading' || importer.state.status === 'uploading'
                  }
                >
                  <Icon name="upload" size={16} />
                  Загрузить срез
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) importer.importFile(f, snapshotDate);
                  }}
                />
              </div>
            )}
          </>
        }
      />

      {importer.state.status !== 'idle' && (
        <div
          className={`${styles.importState} ${importer.state.status === 'error' ? styles.importError : ''}`}
        >
          <span>{importer.state.message}</span>
          {importer.state.status === 'uploading' && (
            <span className={styles.progress}>
              <span style={{ width: `${importer.state.progress}%` }} />
            </span>
          )}
          {(importer.state.status === 'done' || importer.state.status === 'error') && (
            <button className={styles.linkButton} onClick={importer.reset}>
              Скрыть
            </button>
          )}
        </div>
      )}

      {summary.state === 'error' && <div className={styles.errorBox}>{summary.message}</div>}

      {nothingLoaded && (
        <div className={styles.empty}>
          <div className={styles.emptyTitle}>Срез портов ещё не загружен</div>
          <p>
            Загрузите выгрузку отчёта «Устройства на портах коммутаторов» в Excel или CSV. Нужны
            колонки <b>sw_name</b>, <b>Порты</b> и <b>Status</b>; остальные — VLAN, MAC, сведения об
            устройстве — подхватятся, если есть.
          </p>
        </div>
      )}

      {data && !nothingLoaded && (
        <>
          {/* ── Состояние портов: одна полоса, сегменты — фильтр ── */}
          <section className={styles.overview} aria-label="Состояние портов">
            <div className={styles.overviewMain}>
              <div className={styles.overviewTotal}>
                <span className={styles.totalValue}>{kpi.total.toLocaleString('ru')}</span>
                <span className={styles.totalLabel}>
                  портов в выборке, поднято {kpi.total ? Math.round(share(kpi.up)) : 0} %
                </span>
              </div>
              <div className={styles.overviewBar} aria-hidden="true">
                <span className={styles.stUP} style={{ width: `${share(kpi.up)}%` }} />
                <span className={styles.stDOWN} style={{ width: `${share(kpi.down)}%` }} />
                <span className={styles.stADM} style={{ width: `${share(kpi.adm)}%` }} />
              </div>
              <div className={styles.legend}>
                {statusLegend('UP', kpi.up)}
                {statusLegend('DOWN', kpi.down)}
                {statusLegend('ADM', kpi.adm)}
              </div>
            </div>
            <dl className={styles.facts}>
              <div>
                <dt>VLAN в выборке</dt>
                <dd>{(data.totals.vlans || 0).toLocaleString('ru')}</dd>
              </div>
              <div>
                <dt>Устройств опознано</dt>
                <dd>{kpi.devices.toLocaleString('ru')}</dd>
              </div>
              <div>
                <dt>Коммутаторов без расположения</dt>
                <dd>
                  <button
                    className={`${styles.factButton} ${noLocation ? styles.factActive : ''} ${withoutLocation ? styles.factWarn : ''}`}
                    onClick={() => setLocation(noLocation ? '' : NO_LOCATION)}
                    aria-pressed={noLocation}
                    title="Показать коммутаторы без расположения в учёте"
                  >
                    {withoutLocation}
                  </button>
                </dd>
              </div>
            </dl>
          </section>

          <div className={styles.filters}>
            <select
              className={styles.select}
              value={division}
              onChange={(e) => {
                setDivision(e.target.value);
                setMag('');
              }}
            >
              <option value="">Все дивизионы</option>
              {Object.keys(divisions)
                .sort((a, b) => a.localeCompare(b, 'ru', { numeric: true }))
                .map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
            </select>
            <select className={styles.select} value={mag} onChange={(e) => setMag(e.target.value)}>
              <option value="">Все объекты</option>
              {objectOptions.map((m) => (
                <option key={m} value={m}>
                  {objectLabel(m, prefixMap)}
                </option>
              ))}
            </select>
            <select
              className={styles.select}
              value={vlan}
              onChange={(e) => setVlan(e.target.value)}
            >
              <option value="">Все VLAN</option>
              {vlanOptions.map((v) => (
                <option key={v.vlan} value={v.vlan}>
                  VLAN {vlanText(v.vlan, vlanMap)} — {v.ports} порт.
                </option>
              ))}
            </select>
            <select
              className={styles.select}
              value={cabinet}
              onChange={(e) => setCabinet(e.target.value)}
            >
              <option value="">Все шкафы</option>
              {cabinetOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <select
              className={styles.select}
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              aria-label="Расположение"
            >
              <option value="">Все расположения</option>
              <option value={NO_LOCATION}>— не указано —</option>
              {/* Выбранное расположение остаётся в списке, даже если после смены шкафа под него ничего не попадает */}
              {location && location !== NO_LOCATION && !locationOptions.includes(location) && (
                <option value={location}>{location}</option>
              )}
              {locationOptions.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
            <div className={styles.segment}>
              {['', 'UP', 'DOWN', 'ADM'].map((s) => (
                <button
                  key={s || 'all'}
                  className={`${styles.segButton} ${status === s ? styles.segActive : ''}`}
                  onClick={() => setStatus(s)}
                >
                  {s || 'Все'}
                </button>
              ))}
            </div>
            <input
              className={styles.search}
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="MAC, имя компьютера, пользователь…"
            />
            {anyFilter && (
              <button className={styles.linkButton} onClick={resetFilters}>
                Сбросить отбор
              </button>
            )}
          </div>

          <div className={styles.viewSwitch}>
            <button
              className={`${styles.viewButton} ${view === 'switches' ? styles.viewActive : ''}`}
              onClick={() => setView('switches')}
            >
              Коммутаторы <span className={styles.count}>{visible.length}</span>
            </button>
            <button
              className={`${styles.viewButton} ${view === 'devices' ? styles.viewActive : ''}`}
              onClick={() => setView('devices')}
            >
              Устройства
              {devList.state === 'ready' && (
                <span className={styles.count}>{devList.total.toLocaleString('ru')}</span>
              )}
            </button>
            {summary.state === 'refreshing' && <span className={styles.muted}>обновляю…</span>}
            <div className={styles.spacer} />
            {exporting?.error && <span className={styles.exportError}>{exporting.error}</span>}
            <button
              className={ui.button}
              onClick={exportCsv}
              disabled={Boolean(exporting?.busy)}
              title="Все порты по текущим фильтрам — без ограничения в 500 строк. Открывается в Excel"
            >
              {exporting?.busy ? 'Готовлю файл…' : 'Выгрузить в CSV'}
            </button>
          </div>

          {view === 'switches' && (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Объект</th>
                    <th>Коммутатор</th>
                    <th>Шкаф</th>
                    <th className={styles.hideMobile}>Расположение</th>
                    <th>Порты</th>
                    <th className={styles.hideMobile}>VLAN</th>
                    <th className={styles.hideMobile}>Устройств</th>
                    <th />
                  </tr>
                  <tr className={styles.filterRow}>
                    <th>
                      <ColFilter
                        label="Объект"
                        value={colF.object}
                        onChange={setCol('object')}
                        placeholder="Объект"
                      />
                    </th>
                    <th>
                      <ColFilter
                        label="Коммутатор"
                        value={colF.sw}
                        onChange={setCol('sw')}
                        placeholder="Имя или IP"
                      />
                    </th>
                    <th>
                      <ColFilter
                        label="Шкаф"
                        value={colF.cabinet}
                        onChange={setCol('cabinet')}
                        placeholder="Шкаф"
                      />
                    </th>
                    <th className={styles.hideMobile}>
                      <ColFilter
                        label="Расположение"
                        value={colF.location}
                        onChange={setCol('location')}
                        placeholder="Расположение"
                      />
                    </th>
                    <th>
                      <ColFilter
                        label="Загрузка портов, %"
                        numeric
                        value={colF.ports}
                        onChange={setCol('ports')}
                        placeholder="Загрузка, %: >80"
                      />
                    </th>
                    <th className={styles.hideMobile}>
                      <ColFilter
                        label="VLAN"
                        numeric
                        value={colF.vlans}
                        onChange={setCol('vlans')}
                        placeholder=">3"
                      />
                    </th>
                    <th className={styles.hideMobile}>
                      <ColFilter
                        label="Устройств"
                        numeric
                        value={colF.devices}
                        onChange={setCol('devices')}
                        placeholder="0"
                      />
                    </th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((s) => {
                    const key = `${s.mag}__${s.sw}`;
                    const open = expanded === key;
                    return (
                      <Fragment key={key}>
                        <tr
                          className={`${styles.swRow} ${open ? styles.swOpen : ''}`}
                          onClick={() => setExpanded(open ? null : key)}
                        >
                          <td className={styles.cObject}>{objectLabel(s.mag, prefixMap)}</td>
                          <td className={styles.cSwitch}>
                            <div className={styles.mono}>{s.sw}</div>
                            <div className={styles.sub}>{s.sw_ip}</div>
                          </td>
                          <td className={styles.cCabinet} data-label="шкаф">
                            {s.cabinet || <span className={styles.muted}>—</span>}
                          </td>
                          <td className={styles.cLocation}>
                            {s.location || <span className={styles.warnChip}>не указано</span>}
                          </td>
                          <td className={styles.cPorts}>
                            <div
                              className={styles.bar}
                              title={`Поднято ${s.up} · не подключено ${s.down} · отключено ${s.adm}`}
                            >
                              <span
                                className={styles.stUP}
                                style={{ width: `${(s.up / (s.total || 1)) * 100}%` }}
                              />
                              <span
                                className={styles.stDOWN}
                                style={{ width: `${(s.down / (s.total || 1)) * 100}%` }}
                              />
                              <span
                                className={styles.stADM}
                                style={{ width: `${(s.adm / (s.total || 1)) * 100}%` }}
                              />
                            </div>
                            <div className={styles.sub}>
                              {s.up} из {s.total} · {utilization(s)}%
                            </div>
                          </td>
                          <td className={styles.hideMobile}>{s.vlans}</td>
                          <td className={styles.hideMobile}>{s.devices}</td>
                          <td
                            className={`${styles.chevron} ${styles.cChevron}`}
                            aria-label={open ? 'Свернуть' : 'Показать порты'}
                          >
                            {open ? '▴' : '▾'}
                          </td>
                        </tr>
                        {open && (
                          <tr className={styles.expandRow}>
                            <td colSpan={8}>
                              <SwitchPorts mag={s.mag} sw={s.sw} vlan={vlan} status={status} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
              {visible.length === 0 && (
                <div className={styles.none}>Под фильтры ничего не попало.</div>
              )}
            </div>
          )}

          {view === 'devices' && (
            <>
              {devList.state === 'ready' && devList.total > devList.rows.length && (
                <div className={styles.note}>
                  Показано {devList.rows.length} из {devList.total.toLocaleString('ru')} — уточните
                  фильтры или поиск.
                </div>
              )}
              {devList.state === 'error' && (
                <div className={styles.errorBox}>{devList.message}</div>
              )}
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Объект</th>
                      <th>Коммутатор</th>
                      <th>Порт</th>
                      <th>Статус</th>
                      <th>VLAN</th>
                      <th>MAC</th>
                      <th className={styles.hideMobile}>Устройство</th>
                      <th>Имя</th>
                      <th className={styles.hideMobile}>Пользователь</th>
                    </tr>
                    <tr className={styles.filterRow}>
                      <th>
                        <ColFilter
                          label="Объект"
                          value={devF.object}
                          onChange={setDev('object')}
                          placeholder="Объект"
                        />
                      </th>
                      <th>
                        <ColFilter
                          label="Коммутатор"
                          value={devF.sw}
                          onChange={setDev('sw')}
                          placeholder="Коммутатор"
                        />
                      </th>
                      <th>
                        <ColFilter
                          label="Порт"
                          numeric
                          value={devF.port}
                          onChange={setDev('port')}
                          placeholder="1-24"
                        />
                      </th>
                      <th>
                        <select
                          className={`${styles.colFilter} ${status ? styles.colFilterOn : ''}`}
                          value={status}
                          onChange={(e) => setStatus(e.target.value)}
                          aria-label="Фильтр: статус"
                        >
                          <option value="">Все</option>
                          {['UP', 'DOWN', 'ADM'].map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </th>
                      <th>
                        <input
                          className={`${styles.colFilter} ${devF.vlan ? styles.colFilterOn : ''}`}
                          type="search"
                          value={devF.vlan}
                          onChange={(e) => setDev('vlan')(e.target.value)}
                          placeholder="102 или «кассы»"
                          title="Номер VLAN, несколько через запятую или назначение из справочника («кассы»). «-» — без VLAN"
                          aria-label="Фильтр: VLAN"
                        />
                      </th>
                      <th>
                        <ColFilter
                          label="MAC"
                          value={devF.mac}
                          onChange={setDev('mac')}
                          placeholder="MAC"
                        />
                      </th>
                      <th className={styles.hideMobile}>
                        <ColFilter
                          label="Устройство"
                          value={devF.device}
                          onChange={setDev('device')}
                          placeholder="Тип, производитель"
                        />
                      </th>
                      <th>
                        <ColFilter
                          label="Имя"
                          value={devF.name}
                          onChange={setDev('name')}
                          placeholder="Имя ПК"
                        />
                      </th>
                      <th className={styles.hideMobile}>
                        <ColFilter
                          label="Пользователь"
                          value={devF.user}
                          onChange={setDev('user')}
                          placeholder="Логин"
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {devList.rows.map((r) => (
                      <tr key={`${r.mag}__${r.sw}__${r.port}`} className={styles.devRow}>
                        <td className={styles.dObject}>{objectLabel(r.mag, prefixMap)}</td>
                        <td className={`${styles.mono} ${styles.dSwitch}`}>{r.sw}</td>
                        <td className={`${styles.num} ${styles.dPort}`} data-label="порт">
                          {r.port}
                        </td>
                        <td className={styles.dStatus}>
                          <span className={`${styles.pill} ${styles[STATUS_CLASS[r.status]]}`}>
                            {r.status}
                          </span>
                        </td>
                        <td className={styles.dVlan}>
                          {r.vlan && (
                            <span
                              className={styles.vlanChip}
                              style={{ '--vlan': vlanColor(r.vlan) }}
                            >
                              {r.vlan}
                            </span>
                          )}
                          {r.vlan && vlanMap[r.vlan] && (
                            <div className={styles.vlanDesc}>{vlanMap[r.vlan]}</div>
                          )}
                        </td>
                        <td className={styles.dMacs}>
                          <div className={styles.macs}>
                            {splitMacs(r.macs).map((m) => (
                              <span key={m} className={styles.mac}>
                                {m}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className={styles.hideMobile}>
                          {[r.chassis_type, r.manufacturer, r.device_model]
                            .filter(Boolean)
                            .join(' · ')}
                        </td>
                        <td className={`${styles.mono} ${styles.dName}`}>{r.machine_name}</td>
                        <td className={`${styles.muted} ${styles.hideMobile}`}>{r.primary_user}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {devList.state === 'loading' && <div className={styles.none}>Загружаю…</div>}
                {devList.state === 'ready' && devList.rows.length === 0 && (
                  <div className={styles.none}>Ничего не найдено.</div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {vlanDirOpen && (
        <VlanDirectory seen={data?.vlans || []} onClose={() => setVlanDirOpen(false)} />
      )}
    </div>
  );
}
